// Audio: SFX (short-lived HTMLAudio clones), a looping music bed, and a blended ambient
// soundscape (birds / water / wind / fire / chimes) — the zen atmosphere of the original.

const SFX: Record<string, string> = {
  place: '/assets/audio/LowClick.ogg',
  clear: '/assets/audio/Click.ogg',
  bonus: '/assets/audio/MultipleRow.ogg',
  rotatePoint: '/assets/audio/Chimes.mp3',
  drawer: '/assets/audio/OpenDrawer.ogg',
  gameover: '/assets/audio/GameOver.ogg',
  click: '/assets/audio/BasicClick.ogg',
};

// Ambient channels in the ORIGINAL AudioBlend.audioSources order: fire, water, birds, chimes, chirps.
// Per-channel volume comes from the saved mix (Settings.ambientMix); default {50,15,50,25,40}/100.
const AMBIENT_URLS: string[] = [
  '/assets/audio/Fire.mp3',
  '/assets/audio/WaterStream.mp3',
  '/assets/audio/BirdsAndStuff.mp3',
  '/assets/audio/Chimes.mp3',
  '/assets/audio/Chirps.mp3',
];

export class AudioManager {
  private soundVol = 0.8;
  private musicVol = 0.8;
  private ambientOn = true;
  private ambientMix = [0.5, 0.15, 0.5, 0.25, 0.4]; // [fire, water, birds, chimes, chirps]
  private music?: HTMLAudioElement;
  private ambientTracks: HTMLAudioElement[] = [];
  private started = false;
  private cache = new Map<string, HTMLAudioElement>();

  constructor() {
    for (const [k, url] of Object.entries(SFX)) {
      const a = new Audio(url);
      a.preload = 'auto';
      this.cache.set(k, a);
    }
  }

  setLevels(sound: number, music: number, ambient: boolean, mix?: number[]): void {
    this.soundVol = sound;
    this.musicVol = music;
    this.ambientOn = ambient;
    if (mix && mix.length) this.ambientMix = mix;
    this.updateAmbient();
  }

  /** Must be called from a user gesture to satisfy autoplay policies. */
  unlock(): void {
    if (this.started) return;
    this.started = true;
    this.music = new Audio('/assets/audio/Background4.ogg');
    this.music.loop = true;
    for (const url of AMBIENT_URLS) {
      const a = new Audio(url);
      a.loop = true;
      this.ambientTracks.push(a);
    }
    this.updateAmbient(); // starts music XOR ambient per the current setting
  }

  // Original (AudioBlend.ToggleMusicStyle): music and ambient are MUTUALLY EXCLUSIVE.
  // Ambient on -> stop music, play the per-channel ambient mix; ambient off -> play music @0.25.
  // The music slider (musicVol) acts as the master for the ambient bed.
  private updateAmbient(): void {
    if (this.music) {
      this.music.volume = this.musicVol * 0.25; // original backgroundMusic volume 0.25
      if (this.ambientOn) this.music.pause();
      else this.music.play().catch(() => {});
    }
    this.ambientTracks.forEach((a, i) => {
      a.volume = Math.max(0, Math.min(1, (this.ambientMix[i] ?? 0) * this.musicVol));
      if (this.ambientOn) a.play().catch(() => {});
      else a.pause();
    });
  }

  play(
    name: keyof typeof SFX | string,
    opts: { rateJitter?: number; rateRange?: [number, number]; volume?: number } = {},
  ): void {
    const base = this.cache.get(name as string);
    if (!base) return;
    const a = base.cloneNode(true) as HTMLAudioElement;
    a.volume = Math.max(0, Math.min(1, (opts.volume ?? 1) * this.soundVol));
    // rateRange = absolute playbackRate window (e.g. the original's pitch ranges);
    // rateJitter = +/- jitter centred on 1.0.
    if (opts.rateRange) {
      const [lo, hi] = opts.rateRange;
      a.playbackRate = lo + Math.random() * (hi - lo);
    } else if (opts.rateJitter) {
      a.playbackRate = 1 + (Math.random() * 2 - 1) * opts.rateJitter;
    }
    a.play().catch(() => {});
  }

  setMusicEnabled(on: boolean): void {
    if (!this.music) return;
    if (on) this.music.play().catch(() => {});
    else this.music.pause();
  }
}
